import SomethingWentWrong from '@assets/something_went_wrong.svg';
import Calls from '@assets/calls.svg';
import NoResults from '@assets/no_results_search_folder.svg';
import Folder from '@assets/folder.svg';
import {
  BodyText,
  Button,
  Headline,
  Icon,
  IconNameType,
  Stack,
} from '@servicetitan/design-system';
import { ErrorContainer } from './ErrorComponent.styled';
import { strings } from './strings';

interface ErrorComponentProps {
  body?: string | JSX.Element;
  title?: string;
  image?: ErrorImageType | IconNameType;
  imageColor?: string;
  noImage?: boolean;
  primaryAction?: () => void;
  primaryActionLabel?: string;
  secondaryAction?: () => void;
  secondaryActionLabel?: string;
}

type ErrorImageType = keyof typeof ErrorImageMap;

const ErrorImageMap = {
  calls: Calls,
  default: SomethingWentWrong,
  noResults: NoResults,
  folder: Folder,
};

/**
 * Renders an error component with customizable title, body, and actions.
 *
 * @param {string} body - The body text of the error component.
 * @param {string} title - The title text of the error component.
 * @param {Function} primaryAction - The callback function for the primary action button.
 * @param {string} primaryActionLabel - The label text for the primary action button. Will not render primary button if not provided.
 * @param {Function} secondaryAction - The callback function for the secondary action button.
 * @param {string} secondaryActionLabel - The label text for the secondary action button. Will not render secondary button if not provided.
 * @returns {JSX.Element} The rendered error component.
 */
const ErrorComponent = ({
  body = strings.defaultError,
  title = strings.defaultTitle,
  image = 'default',
  primaryAction,
  primaryActionLabel,
  secondaryAction,
  secondaryActionLabel,
  noImage,
  imageColor,
}: ErrorComponentProps) => {
  const isImageErrorType = image in ErrorImageMap;
  const ErrorImage = isImageErrorType
    ? ErrorImageMap[image as ErrorImageType]
    : null;

  const compact = !isImageErrorType;

  return (
    <ErrorContainer>
      <Stack direction='column' spacing={compact ? 2 : 3} alignItems='center'>
        {!noImage && ErrorImage && <ErrorImage color={imageColor} />}
        {!noImage && !ErrorImage && (
          <Icon name={image as IconNameType} size={32} color={imageColor} />
        )}
        <Stack className='ta-center' direction='column' spacing={1}>
          <Headline className='m-b-0' size='medium'>
            {title}
          </Headline>
          {typeof body === 'string' ? <BodyText>{body}</BodyText> : body}
        </Stack>
        <Stack spacing={2}>
          {secondaryActionLabel && (
            <Button className='border-radius-2' onClick={secondaryAction}>
              {strings.labels.logIssue}
            </Button>
          )}
          {primaryActionLabel && (
            <Button
              className='border-radius-2'
              color='primary'
              onClick={primaryAction}
            >
              {primaryActionLabel}
            </Button>
          )}
        </Stack>
      </Stack>
    </ErrorContainer>
  );
};

export default ErrorComponent;
